import Modal from 'bootstrap/js/src/modal.js';
import Service from '@/services/api/models/PlayerProfileRequestService';
import PlayerService from '@/services/api/models/PlayerService';
import { usePlayerProfileRequestsStore } from '@/stores/playerProfileRequests';

export default {
  name: 'PlayerProfileRequestsModal',
  props: { player: { type: Object, required: true }, clubId: { type: String, required: true }, propose: { type: Boolean, default: false } },
  emits: ['close', 'changed'],
  data() {
    return { rows: [], titles: [], access: null, draft: {}, reason: '', comments: {}, editing: false, loading: true, saving: false, error: '', disposed: false };
  },
  computed: {
    playerFullName() {
      const profile = this.access?.profile || this.player;
      return [profile.lastName, profile.firstName].filter(Boolean).join(' ');
    },
    playerId() { return this.player.documentId || this.player.id; },
    pending() { return this.rows.find(row => row.status === 'pending'); },
    fields() { return ['name', 'firstName', 'lastName', 'fideTitle']; },
    hasChanges() { return this.fields.some(field => this.draft[field] !== this.access?.profile?.[field]); },
  },
  mounted() {
    this.returnFocus = document.activeElement;
    this.parentModals = [...document.querySelectorAll('.modal.show')].map(element => Modal.getInstance(element)).filter(Boolean);
    this.parentModals.forEach(modal => modal._focustrap?.deactivate());
    this.modal = new Modal(this.$refs.modal, { backdrop: 'static', keyboard: false });
    this.modal.show();
    this.load().then(() => {
      if (!this.disposed && this.propose && this.access?.sharedProfile && !this.pending && !this.error) this.startEditing();
    });
  },
  beforeUnmount() {
    this.disposed = true;
    this.modal?.hide(); this.modal?.dispose();
    this.parentModals?.forEach(modal => modal._focustrap?.activate());
    if (document.querySelector('.modal.show')) document.body.classList.add('modal-open');
    this.returnFocus?.focus?.();
  },
  methods: {
    resetDraft() { this.draft = { ...this.access?.profile }; this.reason = ''; this.error = ''; },
    startEditing() { this.resetDraft(); this.editing = true; },
    cancelEditing() { this.resetDraft(); this.editing = false; },
    statusIcon(status) {
      return { pending: 'bi-hourglass-split', applied: 'bi-check-circle-fill', rejected: 'bi-x-circle-fill', cancelled: 'bi-slash-circle', stale: 'bi-clock-history' }[status];
    },
    fieldLabel(field) { return this.$t(field === 'fideTitle' ? 'profileRequests.fideTitle' : `playerModal.${field === 'name' ? 'displayName' : field}`); },
    value(row, field, side) {
      const value = row[side][field];
      return field === 'fideTitle' ? row.titleNames?.[value] || value || '—' : value || '—';
    },
    date(value) { return value ? new Date(value).toLocaleString(this.$i18n.locale) : ''; },
    showError(error) {
      const code = error.response?.data?.error?.details?.code;
      const key = `profileRequests.errors.${code}`;
      this.error = code && this.$te(key) ? this.$t(key) : this.$t('profileRequests.failed');
      this.$toast?.add?.({ severity: 'error', summary: this.error, life: 5000 });
    },
    async load() {
      this.loading = true; this.error = '';
      try {
        const [rows, access, titles] = await Promise.all([
          Service.list(this.clubId, this.playerId), PlayerService.getEditAccess(this.clubId, this.playerId), PlayerService.getAssignableTitles(this.clubId),
        ]);
        if (this.disposed) return;
        this.rows = rows; this.access = access; this.titles = titles.filter(title => title.scope === 'fide');
        if (access.fideTitle && !this.titles.some(title => title.documentId === access.fideTitle.documentId)) this.titles.push(access.fideTitle);
        this.draft = { ...access.profile };
        this.loading = false;
        await this.$nextTick();
        if (this.disposed) return;
        const applied = this.rows.filter(row => row.status === 'applied' && row.state === 'result');
        for (const row of applied) {
          await Service.act(this.clubId, row.documentId, 'acknowledge');
          row.state = null;
        }
        if (applied.length) await usePlayerProfileRequestsStore().refresh(this.clubId, this.$user?.id);
      } catch (error) { this.showError(error); }
      finally { this.loading = false; }
    },
    async submit() {
      if (this.saving || !this.hasChanges || !this.reason.trim() || this.pending) return;
      this.saving = true; this.error = '';
      try {
        await Service.create(this.clubId, this.playerId, this.draft, this.reason.trim());
        this.editing = false; this.reason = '';
        await this.updated();
      } catch (error) { this.showError(error); }
      finally { this.saving = false; }
    },
    async act(row, action) {
      if (this.saving) return;
      this.saving = true; this.error = '';
      try {
        await Service.act(this.clubId, row.documentId, action, this.comments[row.documentId] || '');
        await this.updated();
      } catch (error) {
        await this.load();
        this.showError(error);
      } finally { this.saving = false; }
    },
    async updated() {
      await this.load();
      await usePlayerProfileRequestsStore().refresh(this.clubId, this.$user?.id);
      this.$emit('changed', this.access);
    },
  },
};
